import { downloadCsv, fetchAPI } from '@quickbit/backoffice-core-frontend';
import { Currency } from '@quickbit/qb-design-system';
import { useQuery } from '@tanstack/react-query';
import { Order } from 'tabs/Accounting/types';

interface BaseSettlementReportAdjustment {
  amount: number;
  label: string;
}

interface SettlementReportAdjustment extends BaseSettlementReportAdjustment {
  uuid: string;
}

interface SettlementPaymentDetails {
  uuid: string;
  createdAt: Date;
  paidAt: Date;
  euroAmount: number;
  usdtAmount: number | null;
  usdcAmount: number | null;
  reference: string | null;
}

interface SettlementReport {
  uuid: string;
  startTime: Date;
  endTime: Date;
  orderCount: number;
  processed: boolean;
  processingMerchant: boolean;
  settlementAmount: number;
  processingFee: number;
  gatewayFee: number;
  settlementFee: number;
  totalAmount: number;
  refundDebt: number | null;
  maxRefundAmount: number | null;
  amountRemainingToBePaid: number;
  adjustments: SettlementReportAdjustment[];
  payments: SettlementPaymentDetails[];
}

interface Refund {
  refundAmount: number;
  createdAt: Date;
  orderId: string;
  currency: Currency;
}

export type SettlementReportDetails = SettlementReport & {
  orders: Order[];
  refunds: Refund[];
};

const convertToDate = <T extends SettlementReport>(report: T): T => ({
  ...report,
  startTime: new Date(report.startTime),
  endTime: new Date(report.endTime),
  payments: report.payments.map((payment) => ({
    ...payment,
    createdAt: new Date(payment.createdAt),
    paidAt: new Date(payment.paidAt),
  })),
});

export const getSettlementReport = async (
  reportId: string
): Promise<SettlementReportDetails> => {
  const report = await fetchAPI<SettlementReportDetails>(
    `v1/settlement-reports-with-orders/${reportId}`
  );
  const withConvertedDatesReport = convertToDate(report);
  return {
    ...withConvertedDatesReport,
    orders: report.orders.map((order) => ({
      ...order,
      completedAt: order.completedAt ? new Date(order.completedAt) : null,
      createdAt: new Date(order.createdAt),
    })),
    refunds: report.refunds.map((refund) => ({
      ...refund,
      createdAt: new Date(refund.createdAt),
    })),
  };
};

export const useGetSettlementReportSheet = (reportId?: string) => {
  const { data, isLoading } = useQuery({
    enabled: !!reportId,
    queryKey: ['settlement-report-with-orders', reportId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: () => getSettlementReport(reportId!),
  });
  const download = () => {
    if (data) {
      downloadReport(data);
    }
  };
  return { download, isLoading };
};

export const downloadReport = ({
  orders,
  refunds,
  ...report
}: SettlementReportDetails) => {
  const dataArray = [
    'Overview',
    'Currency: Euro',
    'Total sales volume,Settlement amount,Processing fee,Settlement fee,Total order count,Gateway fee,',
    `${report.totalAmount},${report.settlementAmount},${report.processingFee},${report.settlementFee},${report.orderCount},${report.gatewayFee}`,
  ];

  const ordersArray = [
    '',
    'Orders',
    ['Completed at', 'Order ID', 'Reference ID', 'Amount', 'Fee'].join(','),
    ...orders.map((row) =>
      [
        row.completedAt?.toLocaleString().replace(',', ' '),
        row.uuid,
        row.referenceId,
        row.settlement?.euroAmount ?? '-',
        row.settlement?.euroFee ?? '-',
      ].join(',')
    ),
  ];

  const refundsArray = [
    '',
    'Refunds',
    ['Created at', 'Order ID', 'Amount'].join(','),
    ...refunds.map((row) =>
      [
        row.createdAt.toLocaleString().replace(',', ' '),
        row.orderId,
        `${row.refundAmount} ${row.currency}`,
      ].join(',')
    ),
  ];

  const reportArray = [...dataArray, ...ordersArray, ...refundsArray];

  const dateString = `${report.startTime.toISOString()}_${report.endTime.toISOString()}`;
  const csvAsString = reportArray.join('\r\n');
  downloadCsv(csvAsString, `quickbit_settlement_report_${dateString}.csv`);
};
